import { lazy } from 'react';
import { Outlet, Route } from 'react-router-dom';

import { AreaName } from 'domain/constants';
import { OperationType } from 'library/models/enums/OperationType';
import { createArea } from 'library/utils/helpers/AreaHelper';
import Authorize from 'main/auth/Authorize';
import ServiceAreaPage from 'pages/productindividualview/sectionPages/ServiceAreaPage';

import { PageRedirect } from './PageRedirect';
import LazyRouteElement from './common/LazyRoute';
import {
	ProductIndividual_Services_Repairs,
	productIndividualCampaigns,
	productIndividualClaims,
	productIndividualConfiguration,
	productIndividualDocuments,
	productIndividualEdit,
	productIndividualEventLog,
	productIndividualLogisticTrail,
	productIndividualParts,
	productIndividualPolicy,
	productIndividualServiceArea,
	productIndividualTechnical,
	productIndividualVstCodes,
	productIndividualviewSNParam,
} from './urls/RouteUrls';

const ProductIndividualviewPage = lazy(
	() => import('pages/productindividualview/ProductIndividualviewPage')
);

const ProductIndividualviewEditPage = lazy(
	() => import('pages/productindividualview/ProductIndividualviewEditPage')
);

const TechnicalSectionPage = lazy(
	() =>
		import('pages/productindividualview/sectionPages/TechnicalSectionPage')
);

const CampaignsSectionPage = lazy(
	() =>
		import('pages/productindividualview/sectionPages/CampaignsSectionPage')
);
const ClaimsSectionPage = lazy(
	() => import('pages/productindividualview/sectionPages/ClaimsSectionPage')
);
const PartsSectionPage = lazy(
	() => import('pages/productindividualview/sectionPages/PartsSectionPage')
);
const ConfigurationSectionPage = lazy(
	() =>
		import(
			'pages/productindividualview/sectionPages/ConfigurationSectionPage'
		)
);
const EventLogSectionPage = lazy(
	() => import('pages/productindividualview/sectionPages/EventLogSectionPage')
);
const LogisticTrailSectionPage = lazy(
	() =>
		import(
			'pages/productindividualview/sectionPages/LogisticTrailSectionPage'
		)
);

const VstCodesSectionPage = lazy(
	() => import('pages/productindividualview/sectionPages/VstCodesSectionPage')
);

const RepairsSectionPage = lazy(
	() =>
		import(
			'pages/productindividualview/sectionPages/Services/RepairsSectionPage'
		)
);

const PolicySectionPage = lazy(
	() => import('pages/productindividualview/sectionPages/PolicySectionPage')
);

const DocumentsSectionPage = lazy(
	() =>
		import('pages/productindividualview/sectionPages/DocumentsSectionPage')
);

export const getProductIndividualRoutes = (): React.JSX.Element[] => [
	<Route
		key="productindividualview"
		element={
			<Authorize
				area={createArea(AreaName.Individual, OperationType.Read)}>
				<Authorize area={createArea(AreaName.Unit, OperationType.Read)}>
					<Outlet />
				</Authorize>
			</Authorize>
		}>
		<Route
			path={`${productIndividualviewSNParam}`}
			element={<ProductIndividualviewPage />}>
			<Route
				index
				element={<PageRedirect url={productIndividualCampaigns} />}
			/>
			<Route
				path={productIndividualTechnical}
				element={
					<LazyRouteElement
						Component={TechnicalSectionPage}
						path={productIndividualTechnical}
					/>
				}
			/>
			<Route
				path={productIndividualCampaigns}
				element={
					<LazyRouteElement
						Component={CampaignsSectionPage}
						path={productIndividualCampaigns}
					/>
				}
			/>
			<Route
				path={productIndividualClaims}
				element={
					<LazyRouteElement
						area={createArea(
							AreaName.IndividualViewClaim,
							OperationType.Read
						)}
						Component={ClaimsSectionPage}
						path={productIndividualClaims}
					/>
				}
			/>

			<Route
				path={productIndividualParts}
				element={
					<LazyRouteElement
						Component={PartsSectionPage}
						path={productIndividualParts}
					/>
				}
			/>
			<Route
				path={productIndividualConfiguration}
				element={
					<LazyRouteElement
						Component={ConfigurationSectionPage}
						path={productIndividualConfiguration}
					/>
				}
			/>
			<Route
				path={productIndividualEventLog}
				element={
					<LazyRouteElement
						area={createArea(
							AreaName.ProductIndividualEventLog,
							OperationType.Read
						)}
						Component={EventLogSectionPage}
						path={productIndividualEventLog}
					/>
				}
			/>
			<Route
				path={productIndividualLogisticTrail}
				element={
					<LazyRouteElement
						area={createArea(
							AreaName.LogisticTrail,
							OperationType.Read
						)}
						Component={LogisticTrailSectionPage}
						path={productIndividualLogisticTrail}
					/>
				}
			/>
			<Route
				path={productIndividualVstCodes}
				element={
					<LazyRouteElement
						Component={VstCodesSectionPage}
						path={productIndividualVstCodes}
					/>
				}
			/>
			<Route
				path={productIndividualDocuments}
				element={
					<LazyRouteElement
						area={createArea(
							AreaName.UploadProductionAndClassificationDocuments,
							OperationType.Read
						)}
						Component={DocumentsSectionPage}
						path={productIndividualDocuments}
					/>
				}
			/>
			<Route
				path={ProductIndividual_Services_Repairs}
				element={<PageRedirect url={productIndividualServiceArea} />}
			/>
			<Route path={productIndividualServiceArea}>
				<Route element={<ServiceAreaPage />}>
					<Route
						index
						element={
							<PageRedirect
								url={ProductIndividual_Services_Repairs}
							/>
						}
					/>
					<Route
						path={ProductIndividual_Services_Repairs}
						element={
							<LazyRouteElement
								area={createArea(
									AreaName.Repair,
									OperationType.Read
								)}
								Component={RepairsSectionPage}
								path={ProductIndividual_Services_Repairs}
							/>
						}
					/>
				</Route>
			</Route>
			<Route
				path={productIndividualPolicy}
				element={
					<LazyRouteElement
						Component={PolicySectionPage}
						path={productIndividualPolicy}
					/>
				}
			/>
		</Route>
	</Route>,
	<Route
		key="productindividualeditview"
		element={
			<Authorize
				area={createArea(AreaName.Individual, OperationType.Write)}>
				<ProductIndividualviewEditPage />
			</Authorize>
		}
		path={productIndividualEdit}
	/>,
];
